import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getSubjects, getChapterCompletion, uploadChapterNotes,
  setChapterComplete, getChapterNotes, buildDailyTest,
} from '../../api';

// Chapter Completion: the admin marks a chapter finished by uploading its notes.
// A chapter is NOT complete without notes — the Daily Test only draws from
// complete chapters, and the AI writes fresh questions from NCERT + these notes.

export default function AdminChapters() {
  const [subjectId, setSubjectId] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState(null);         // {kind:'ok'|'err', text}
  const [preview, setPreview] = useState(null); // {chapter, text}
  const fileInputs = useRef({});

  const { data: subjects } = useQuery({
    queryKey: ['subjects'],
    queryFn: () => getSubjects().then(r => r.data),
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['chapterCompletion', subjectId],
    queryFn: () => getChapterCompletion(subjectId || undefined).then(r => r.data),
  });

  const onPick = async (chapter, file) => {
    if (!file) return;
    setBusyId(chapter.chapter_id); setMsg(null);
    try {
      const r = await uploadChapterNotes(chapter.chapter_id, file, true);
      setMsg({ kind: 'ok', text: `Notes saved for ${chapter.chapter_name} — ${r.data.notes_chars.toLocaleString()} characters read. Chapter marked complete.` });
      refetch();
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Upload failed.' });
    } finally {
      setBusyId(null);
      if (fileInputs.current[chapter.chapter_id]) fileInputs.current[chapter.chapter_id].value = '';
    }
  };

  const toggle = async (chapter) => {
    setBusyId(chapter.chapter_id); setMsg(null);
    try {
      await setChapterComplete(chapter.chapter_id, !chapter.is_complete);
      refetch();
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Could not change that.' });
    } finally { setBusyId(null); }
  };

  const showNotes = async (chapter) => {
    setMsg(null);
    try {
      const r = await getChapterNotes(chapter.chapter_id);
      setPreview({ chapter: chapter.chapter_name, text: r.data.notes_text });
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'No notes to show.' });
    }
  };

  const buildNow = async () => {
    setMsg(null);
    try {
      const r = await buildDailyTest(true);
      setMsg(r.data.created
        ? { kind: 'ok', text: `Daily test built: ${r.data.total_questions} questions (${r.data.wrong} wrong before, ${r.data.unattempted} skipped, ${r.data.unseen || 0} unseen, ${r.data.fresh} newly written).` }
        : { kind: 'err', text: r.data.reason || 'Nothing to build.' });
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Could not build the daily test.' });
    }
  };

  const chapters = data?.chapters || [];
  const done = chapters.filter(c => c.is_complete).length;

  if (preview) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ fontSize: 22 }}>Notes — {preview.chapter}</h1>
          <button className="btn btn-ghost btn-sm" onClick={() => setPreview(null)}>Back</button>
        </div>
        <div className="card">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
            This is the text the system read out of your file. It is what the AI uses, alongside
            NCERT, to write new questions. If it looks wrong or empty, upload a different file.
          </p>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6, maxHeight: 500, overflow: 'auto' }}>
            {preview.text}
          </pre>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 26 }}>✅ Chapter Completion &amp; Notes</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          Upload a chapter's notes to mark it complete. Only complete chapters go into Harsh's
          Daily Test, and the notes are what new questions are written from. Accepts PDF, Word
          (.docx) or plain text — the notes must contain real text, not scanned images.
        </p>
      </div>

      {msg && (
        <div className="card" style={{
          marginBottom: 16,
          borderColor: msg.kind === 'ok' ? 'var(--success)' : 'var(--danger)',
          color: msg.kind === 'ok' ? 'var(--success)' : 'var(--danger)',
        }}>{msg.text}</div>
      )}

      <div className="card" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: 'var(--text-muted)' }}>Subject</label>
        <select value={subjectId} onChange={e => setSubjectId(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)' }}>
          <option value="">All subjects</option>
          {(subjects || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <span className="badge badge-green">{done} complete</span>
        <span style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" onClick={buildNow}>
          Build today's daily test now
        </button>
      </div>

      {isLoading ? <div className="spinner"></div> : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Chapter</th>
                <th style={{ textAlign: 'center' }}>Questions</th>
                <th>Notes</th>
                <th style={{ textAlign: 'right' }}>Complete</th>
              </tr>
            </thead>
            <tbody>
              {chapters.map(c => (
                <tr key={c.chapter_id}>
                  <td>
                    {c.chapter_name}
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.subject_name}</div>
                  </td>
                  <td style={{ textAlign: 'center' }}>{c.question_count}</td>
                  <td>
                    {c.has_notes ? (
                      <div>
                        <button className="btn btn-ghost btn-sm" onClick={() => showNotes(c)}>
                          {c.notes_filename || 'View notes'}
                        </button>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {c.notes_chars.toLocaleString()} characters
                          {c.notes_uploaded_at && ` · ${new Date(c.notes_uploaded_at).toLocaleDateString()}`}
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>none yet</span>
                    )}
                    <input
                      type="file"
                      accept=".pdf,.docx,.txt,.md"
                      ref={el => { fileInputs.current[c.chapter_id] = el; }}
                      onChange={e => onPick(c, e.target.files[0])}
                      disabled={busyId === c.chapter_id}
                      style={{ display: 'block', marginTop: 6, fontSize: 12 }}
                    />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {c.is_complete ? (
                      <>
                        <span className="badge badge-green">Complete</span>
                        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }}
                                disabled={busyId === c.chapter_id} onClick={() => toggle(c)}>
                          Undo
                        </button>
                      </>
                    ) : (
                      <button className="btn btn-ghost btn-sm"
                              disabled={busyId === c.chapter_id || !c.has_notes}
                              title={c.has_notes ? '' : 'Upload the notes first'}
                              onClick={() => toggle(c)}>
                        Mark complete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
